import {
  Sparkles,
  Download,
  Settings,
  Crown,
  CheckCircle2,
} from 'lucide-react';
import CloseButton from '../button/CloseButton';
import AppStoreButton from '../button/AppStoreButton';
import PlayStoreButton from '../button/PlayStoreButton';

type PremiumUpgradeModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

const steps = [
  {
    number: 1,
    icon: Download,
    title: 'Download the App',
    description:
      'Get WhenWeMeet from the App Store or Google Play using the buttons below.',
    iconBg: 'bg-blue-100',
    iconColor: 'text-blue-600',
  },
  {
    number: 2,
    icon: Settings,
    title: 'Open Settings',
    description: 'Launch the app and navigate to the Settings section.',
    iconBg: 'bg-purple-100',
    iconColor: 'text-purple-600',
  },
  {
    number: 3,
    icon: Crown,
    title: 'Select Upgrade Plan',
    description:
      'In Settings, find the Premium section and tap on "Upgrade Plan".',
    iconBg: 'bg-amber-100',
    iconColor: 'text-amber-600',
  },
  {
    number: 4,
    icon: CheckCircle2,
    title: 'Complete Upgrade',
    description:
      'Follow the in-app instructions to finish your Premium upgrade.',
    iconBg: 'bg-green-100',
    iconColor: 'text-green-600',
  },
];

export default function PremiumUpgradeModal({
  isOpen,
  onClose,
}: PremiumUpgradeModalProps) {
  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-2 sm:p-4"
      onClick={onClose}>
      <div
        className="relative bg-white w-full max-w-3xl max-h-[calc(100dvh-1rem)] sm:max-h-[90vh] rounded-3xl shadow-xl overflow-y-auto overscroll-y-none hide-scrollbar"
        onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="sticky top-0 z-10 bg-green-600 rounded-t-2xl sm:rounded-t-3xl px-6 sm:px-8 pt-4 sm:pt-6 pb-6 sm:pb-8 overflow-hidden">
          <CloseButton
            onClose={onClose}
            variant="secondary"
            className="right-4 sm:right-6 top-4 sm:top-6 z-10"
          />
          <div className="absolute inset-0 opacity-20 pointer-events-none" />

          <div className="relative flex text-left gap-3 sm:gap-4">
            <div className="flex-shrink-0 w-12 h-12 sm:w-16 sm:h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
              <Sparkles className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
            </div>
            <div>
              <h2 className="text-xl sm:text-3xl font-extrabold text-white mb-1">
                Upgrade to Premium
              </h2>
              <p className="text-green-50 text-sm sm:text-base">
                Unlock an ad-free, premium experience
              </p>
            </div>
          </div>
        </div>

        <div className="py-6 px-4 sm:px-8">
          {/* Steps */}
          <div>
            <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-6">
              How to Upgrade
            </h3>

            <div className="space-y-4">
              {steps.map((step) => {
                const IconComponent = step.icon;
                return (
                  <div
                    key={step.number}
                    className="flex gap-4 p-4 rounded-xl border border-gray-200 bg-gradient-to-r from-white to-gray-50/50 hover:shadow-md transition-all hover:border-gray-300">
                    {/* Step number and icon */}
                    <div className="flex-shrink-0">
                      <div className="relative">
                        <div
                          className={`w-12 h-12 sm:w-14 sm:h-14 rounded-xl ${step.iconBg} flex items-center justify-center`}>
                          <IconComponent
                            className={`w-6 h-6 sm:w-7 sm:h-7 ${step.iconColor}`}
                          />
                        </div>
                        <div className="absolute -top-2 -right-2 w-6 h-6 sm:w-7 sm:h-7 bg-green-600 text-white rounded-full flex items-center justify-center text-xs sm:text-sm font-bold shadow-lg">
                          {step.number}
                        </div>
                      </div>
                    </div>

                    {/* Step content */}
                    <div className="flex-1 pt-1 text-left">
                      <h4 className="text-base sm:text-lg font-semibold text-gray-900 mb-1">
                        {step.title}
                      </h4>
                      <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                        {step.description}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Divider */}
          <div className="relative my-4">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-200"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="bg-white px-4 text-gray-500 font-medium">
                Get Started
              </span>
            </div>
          </div>

          {/* Download buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <AppStoreButton />
            <PlayStoreButton />
          </div>
        </div>
      </div>
    </div>
  );
}
